// ============================================================
// src/components/admin/DoctorAccountTab.js
// "Doctor Accounts" tab — fully responsive for mobile + desktop
// ============================================================
import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";

// ── Shared inline styles ──────────────────────────────────────
const INP = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  fontSize: 13,
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "'DM Sans',sans-serif",
  background: "#fff",
};
const LBL = {
  display: "block",
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: 5,
};
const BTN_PRIMARY = {
  padding: "11px 24px",
  borderRadius: 8,
  border: "none",
  background: "linear-gradient(135deg,#0a1628,#1a3a6b)",
  color: "#fff",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
  width: "100%",
};
const BTN_GHOST = {
  padding: "11px 18px",
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#f8fafc",
  color: "#374151",
  fontWeight: 600,
  fontSize: 13,
  cursor: "pointer",
  fontFamily: "'DM Sans',sans-serif",
  width: "100%",
};

const SPECIALTIES = [
  "Chief Dental Surgeon",
  "Cosmetic & Aesthetic Dentist",
  "Orthodontics & Invisalign",
  "Root Canal Specialist",
  "Pediatric Dentist",
  "Oral & Maxillofacial Surgeon",
  "Periodontics Specialist",
  "Prosthodontics",
  "General Dentistry",
  "Dental Implant Specialist",
  "Other",
];

// Strength checker for password
function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "#e5e7eb" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (pw.length >= 12) score++;
  const map = [
    { label: "Very Weak", color: "#ef4444" },
    { label: "Weak", color: "#f97316" },
    { label: "Fair", color: "#eab308" },
    { label: "Good", color: "#22c55e" },
    { label: "Strong", color: "#16a34a" },
  ];
  return { score, ...map[Math.min(score, 4)] };
}

// ── Single doctor card ────────────────────────────────────────
function DoctorCard({ doc, onDelete, currentDoctorId }) {
  const isMe = doc.doctorId === currentDoctorId;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: "14px 16px",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        borderLeft: isMe ? "4px solid #0d7377" : "1px solid #e5e7eb",
      }}
    >
      {/* Avatar */}
      <div
        style={{
          width: 46,
          height: 46,
          borderRadius: "50%",
          flexShrink: 0,
          background: isMe
            ? "linear-gradient(135deg,#0d7377,#14a0a5)"
            : "linear-gradient(135deg,#1e40af,#3b82f6)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 20,
          color: "#fff",
          fontWeight: 700,
        }}
      >
        {(doc.name || "?")
          .replace(/^Dr\.\s*/i, "")
          .charAt(0)
          .toUpperCase()}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, color: "#0a1628" }}>
            {doc.name}
          </div>
          {isMe && (
            <span
              style={{
                background: "rgba(13,115,119,0.12)",
                border: "1px solid rgba(13,115,119,0.3)",
                color: "#0d7377",
                borderRadius: 20,
                fontSize: 10,
                fontWeight: 800,
                padding: "2px 8px",
                letterSpacing: "0.5px",
              }}
            >
              YOU
            </span>
          )}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#0d7377",
            fontWeight: 600,
            marginTop: 2,
          }}
        >
          {doc.specialty || "—"}
        </div>
        <div
          style={{
            marginTop: 6,
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <span
            style={{
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
              fontSize: 11,
              padding: "3px 8px",
              color: "#475569",
              fontWeight: 600,
              fontFamily: "monospace",
            }}
          >
            ID: {doc.doctorId}
          </span>
          {doc.createdAt && (
            <span style={{ fontSize: 11, color: "#94a3b8" }}>
              Joined{" "}
              {new Date(doc.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
          )}
        </div>
      </div>

      {/* Delete */}
      {!isMe && (
        <button
          onClick={() => onDelete(doc)}
          style={{
            padding: "6px 12px",
            borderRadius: 7,
            border: "1px solid #fca5a5",
            background: "#fef2f2",
            color: "#dc2626",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          Delete
        </button>
      )}
    </div>
  );
}

// ── Create Account Form ───────────────────────────────────────
function CreateAccountForm({ onCreated, showNotify }) {
  const [form, setForm] = useState({
    name: "",
    specialty: "",
    doctorId: "",
    password: "",
    confirmPw: "",
  });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);

  const sf = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const strength = passwordStrength(form.password);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      showNotify("Error", "Full name is required.", true);
      return;
    }
    if (!form.specialty) {
      showNotify("Error", "Please select a specialty.", true);
      return;
    }
    if (!form.doctorId.trim()) {
      showNotify("Error", "Doctor ID is required.", true);
      return;
    }
    if (form.doctorId.includes(" ")) {
      showNotify("Error", "Doctor ID cannot contain spaces.", true);
      return;
    }
    if (!form.password) {
      showNotify("Error", "Password is required.", true);
      return;
    }
    if (form.password.length < 6) {
      showNotify("Error", "Password must be at least 6 characters.", true);
      return;
    }
    if (form.password !== form.confirmPw) {
      showNotify("Error", "Passwords do not match.", true);
      return;
    }

    setSaving(true);
    try {
      await api.post("/auth/register", {
        name: form.name.trim(),
        specialty: form.specialty,
        doctorId: form.doctorId.trim().toLowerCase(),
        password: form.password,
      });
      showNotify(
        "Account Created! ✓",
        `Dr. ${form.name} can now log in with ID: ${form.doctorId.toLowerCase()}`,
      );
      setForm({
        name: "",
        specialty: "",
        doctorId: "",
        password: "",
        confirmPw: "",
      });
      onCreated();
    } catch (err) {
      showNotify("Error", err.message || "Failed to create account.", true);
    }
    setSaving(false);
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: "20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: "#0a1628",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            background: "linear-gradient(135deg,#0a1628,#1a3a6b)",
            color: "#fff",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 16,
          }}
        >
          🩺
        </span>
        Create New Doctor Account
      </div>

      {/* Fields — all single column on mobile */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Full Name */}
        <div>
          <label style={LBL}>Full Name *</label>
          <input
            value={form.name}
            onChange={(e) => sf("name", e.target.value)}
            placeholder="e.g. Dr. Neha Joshi"
            style={INP}
          />
        </div>

        {/* Specialty */}
        <div>
          <label style={LBL}>Specialty *</label>
          <select
            value={form.specialty}
            onChange={(e) => sf("specialty", e.target.value)}
            style={INP}
          >
            <option value="">— Select Specialty —</option>
            {SPECIALTIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* Doctor ID */}
        <div>
          <label style={LBL}>Doctor ID (Login Username) *</label>
          <input
            value={form.doctorId}
            onChange={(e) => sf("doctorId", e.target.value.replace(/\s/g, ""))}
            placeholder="e.g. drneha"
            style={{ ...INP, fontFamily: "monospace", letterSpacing: "0.5px" }}
          />
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>
            No spaces. Doctor types this at login screen.
          </div>
        </div>

        {/* Password */}
        <div>
          <label style={LBL}>Password *</label>
          <div style={{ position: "relative" }}>
            <input
              type={showPw ? "text" : "password"}
              value={form.password}
              onChange={(e) => sf("password", e.target.value)}
              placeholder="Min. 6 characters"
              style={{ ...INP, paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((p) => !p)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "#94a3b8",
              }}
            >
              {showPw ? "🙈" : "👁️"}
            </button>
          </div>
          {form.password && (
            <div style={{ marginTop: 6 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: "#e5e7eb",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 2,
                    width: `${(strength.score / 4) * 100}%`,
                    background: strength.color,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: strength.color,
                  fontWeight: 700,
                  marginTop: 3,
                }}
              >
                {strength.label}
              </div>
            </div>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label style={LBL}>Confirm Password *</label>
          <div style={{ position: "relative" }}>
            <input
              type={showCpw ? "text" : "password"}
              value={form.confirmPw}
              onChange={(e) => sf("confirmPw", e.target.value)}
              placeholder="Re-enter password"
              style={{
                ...INP,
                paddingRight: 40,
                borderColor:
                  form.confirmPw && form.password !== form.confirmPw
                    ? "#ef4444"
                    : form.confirmPw && form.password === form.confirmPw
                      ? "#22c55e"
                      : "#d1d5db",
              }}
            />
            <button
              type="button"
              onClick={() => setShowCpw((p) => !p)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "#94a3b8",
              }}
            >
              {showCpw ? "🙈" : "👁️"}
            </button>
          </div>
          {form.confirmPw && (
            <div
              style={{
                fontSize: 10,
                marginTop: 3,
                fontWeight: 700,
                color: form.password === form.confirmPw ? "#22c55e" : "#ef4444",
              }}
            >
              {form.password === form.confirmPw
                ? "✓ Passwords match"
                : "✗ Passwords do not match"}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px" }}>
            <button
              onClick={handleCreate}
              disabled={saving}
              style={{ ...BTN_PRIMARY, opacity: saving ? 0.7 : 1 }}
            >
              {saving ? "Creating..." : "🔐 Create Account"}
            </button>
          </div>
          <div style={{ flex: "1 1 100px" }}>
            <button
              onClick={() =>
                setForm({
                  name: "",
                  specialty: "",
                  doctorId: "",
                  password: "",
                  confirmPw: "",
                })
              }
              style={BTN_GHOST}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Change Password Form ──────────────────────────────────────
function ChangePasswordForm({ currentDoctor, showNotify }) {
  const [form, setForm] = useState({ oldPw: "", newPw: "", confirmPw: "" });
  const [saving, setSaving] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const sf = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const strength = passwordStrength(form.newPw);

  const handleChange = async () => {
    if (!form.oldPw) {
      showNotify("Error", "Enter your current password.", true);
      return;
    }
    if (!form.newPw) {
      showNotify("Error", "Enter a new password.", true);
      return;
    }
    if (form.newPw.length < 6) {
      showNotify("Error", "New password must be at least 6 characters.", true);
      return;
    }
    if (form.newPw !== form.confirmPw) {
      showNotify("Error", "New passwords do not match.", true);
      return;
    }
    if (form.oldPw === form.newPw) {
      showNotify("Error", "New password must be different.", true);
      return;
    }

    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        oldPassword: form.oldPw,
        newPassword: form.newPw,
      });
      showNotify(
        "Password Changed! ✓",
        "Your new password is active. Use it next time you log in.",
      );
      setForm({ oldPw: "", newPw: "", confirmPw: "" });
    } catch (err) {
      showNotify("Error", err.message || "Failed to change password.", true);
    }
    setSaving(false);
  };

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: "20px",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
      }}
    >
      {/* Header */}
      <div
        style={{
          fontSize: 14,
          fontWeight: 800,
          color: "#0a1628",
          marginBottom: 4,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            background: "linear-gradient(135deg,#c9a84c,#d4af37)",
            color: "#fff",
            borderRadius: 8,
            padding: "6px 10px",
            fontSize: 16,
          }}
        >
          🔑
        </span>
        Change My Password
      </div>
      <div
        style={{
          fontSize: 11,
          color: "#64748b",
          fontWeight: 500,
          background: "#f1f5f9",
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          padding: "6px 12px",
          marginBottom: 16,
          display: "inline-block",
        }}
      >
        Logged in as: <strong>{currentDoctor?.name}</strong>
      </div>

      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Current Password */}
        <div>
          <label style={LBL}>Current Password</label>
          <div style={{ position: "relative" }}>
            <input
              type={showOld ? "text" : "password"}
              value={form.oldPw}
              onChange={(e) => sf("oldPw", e.target.value)}
              placeholder="Your current password"
              style={{ ...INP, paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowOld((p) => !p)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "#94a3b8",
              }}
            >
              {showOld ? "🙈" : "👁️"}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label style={LBL}>New Password</label>
          <div style={{ position: "relative" }}>
            <input
              type={showNew ? "text" : "password"}
              value={form.newPw}
              onChange={(e) => sf("newPw", e.target.value)}
              placeholder="Min. 6 characters"
              style={{ ...INP, paddingRight: 40 }}
            />
            <button
              type="button"
              onClick={() => setShowNew((p) => !p)}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                color: "#94a3b8",
              }}
            >
              {showNew ? "🙈" : "👁️"}
            </button>
          </div>
          {form.newPw && (
            <div style={{ marginTop: 4 }}>
              <div
                style={{
                  height: 3,
                  borderRadius: 2,
                  background: "#e5e7eb",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 2,
                    width: `${(strength.score / 4) * 100}%`,
                    background: strength.color,
                  }}
                />
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: strength.color,
                  fontWeight: 700,
                  marginTop: 2,
                }}
              >
                {strength.label}
              </div>
            </div>
          )}
        </div>

        {/* Confirm New Password */}
        <div>
          <label style={LBL}>Confirm New Password</label>
          <input
            type="password"
            value={form.confirmPw}
            onChange={(e) => sf("confirmPw", e.target.value)}
            placeholder="Re-enter new password"
            style={{
              ...INP,
              borderColor:
                form.confirmPw && form.newPw !== form.confirmPw
                  ? "#ef4444"
                  : form.confirmPw && form.newPw === form.confirmPw
                    ? "#22c55e"
                    : "#d1d5db",
            }}
          />
          {form.confirmPw && (
            <div
              style={{
                fontSize: 10,
                marginTop: 3,
                fontWeight: 700,
                color: form.newPw === form.confirmPw ? "#22c55e" : "#ef4444",
              }}
            >
              {form.newPw === form.confirmPw ? "✓ Match" : "✗ No match"}
            </div>
          )}
        </div>

        {/* Button */}
        <button
          onClick={handleChange}
          disabled={saving}
          style={{
            ...BTN_PRIMARY,
            background: "linear-gradient(135deg,#92400e,#c9a84c)",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving..." : "🔑 Update Password"}
        </button>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function DoctorAccountTab({ showNotify, doctor }) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/auth/doctors");
      setDoctors(Array.isArray(data) ? data : []);
    } catch (err) {
      showNotify("Error", err.message, true);
    }
    setLoading(false);
  }, [showNotify]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (doc) => {
    if (
      !window.confirm(
        `Delete account for ${doc.name}?\n\nThey will no longer be able to log in.`,
      )
    )
      return;
    try {
      await api.delete("/auth/doctors/" + doc._id);
      showNotify("Deleted", `${doc.name}'s account removed.`);
      load();
    } catch (err) {
      showNotify("Error", err.message, true);
    }
  };

  return (
    <div style={{ paddingBottom: 48 }}>
      {/* Responsive styles injected once */}
      <style>{`
        .da-outer-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          align-items: start;
        }
        .da-steps-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }
        @media (max-width: 768px) {
          .da-outer-grid {
            grid-template-columns: 1fr !important;
          }
          .da-steps-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* Page header */}
      <div className="admin-section-title">Doctor Accounts</div>
      <div className="admin-section-desc" style={{ marginBottom: 20 }}>
        Each doctor can create their own login account here. Once created, they
        can log in using their
        <strong> Doctor ID + Password</strong>. Their name will automatically
        appear in all patient records, invoices, prescriptions, and visit
        history across the system.
      </div>

      {/* How it works banner */}
      <div
        style={{
          background:
            "linear-gradient(135deg,rgba(10,22,40,0.04),rgba(13,115,119,0.06))",
          border: "1px solid rgba(13,115,119,0.2)",
          borderRadius: 12,
          padding: "14px 16px",
          marginBottom: 20,
        }}
      >
        <div className="da-steps-grid">
          {[
            {
              icon: "1️⃣",
              text: "Doctor fills in their name, specialty, Doctor ID and password below",
            },
            {
              icon: "2️⃣",
              text: 'Click "Create Account" — saved instantly to database',
            },
            {
              icon: "3️⃣",
              text: "Doctor goes to login screen → types their Doctor ID + password → access granted",
            },
            {
              icon: "4️⃣",
              text: "Their name now appears on all visits, bills, prescriptions automatically",
            },
          ].map((s) => (
            <div
              key={s.icon}
              style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{s.icon}</span>
              <span style={{ fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
                {s.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Main 2-col grid (stacks on mobile) */}
      <div className="da-outer-grid">
        {/* LEFT: Forms */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <CreateAccountForm onCreated={load} showNotify={showNotify} />
          <ChangePasswordForm currentDoctor={doctor} showNotify={showNotify} />
        </div>

        {/* RIGHT: Doctor list */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <span
                style={{
                  background: "linear-gradient(135deg,#0a1628,#1a3a6b)",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "4px 10px",
                  fontSize: 13,
                  fontWeight: 800,
                }}
              >
                {loading ? "..." : doctors.length}
              </span>
              Doctor{doctors.length !== 1 ? "s" : ""} in system
            </div>
            <button
              onClick={load}
              style={{
                padding: "7px 14px",
                borderRadius: 8,
                border: "1px solid #d1d5db",
                background: "#f8fafc",
                color: "#374151",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              🔄 Refresh
            </button>
          </div>

          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: 32,
                color: "#94a3b8",
                fontSize: 13,
              }}
            >
              Loading accounts...
            </div>
          ) : doctors.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 40,
                background: "#f8fafc",
                borderRadius: 14,
                color: "#94a3b8",
                fontSize: 13,
                border: "1px dashed #e2e8f0",
              }}
            >
              No doctor accounts yet.
              <br />
              Create the first one using the form.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {doctors.map((doc) => (
                <DoctorCard
                  key={doc._id}
                  doc={doc}
                  onDelete={handleDelete}
                  currentDoctorId={doctor?.doctorId}
                />
              ))}
            </div>
          )}

          {/* Security reminder */}
          {doctors.length > 0 && (
            <div
              style={{
                marginTop: 16,
                background: "rgba(234,179,8,0.07)",
                border: "1px solid rgba(234,179,8,0.3)",
                borderRadius: 10,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: "#92400e",
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                🔒 Security Reminder
              </div>
              <div style={{ fontSize: 11, color: "#78350f", lineHeight: 1.7 }}>
                • Each doctor should keep their password private.
                <br />
                • To reset a doctor's password, delete their account and create
                a new one.
                <br />• You cannot see saved passwords — only the doctor knows
                theirs.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
